/**
 * Turns an error thrown by a save API call into a user-friendly message shown
 * above the Save CTA on the recording/release edit form.
 *
 * The generated API client throws a `ResponseError` (carrying the raw fetch
 * `Response`) for any non-2xx. Backend save failures return HTTP 400 with one
 * of these body shapes:
 *  - service failure:  { success:false, …, errors: string[] }
 *  - DRF validation:   { <field>: ["msg"], … } (sometimes nested under `payload`)
 *  - misc:             { error: "…" }
 * The raw error text often contains DynamoDB / Neptune-Gremlin / OpenSearch
 * detail that we map to plain-language guidance, with a vague fallback for
 * anything we don't recognise.
 */

export type SaveErrorContext =
  | 'recording'
  | 'release'
  | 'artists'
  | 'contributions'
  | 'release-link';

const CONTEXT_NOUN: Record<SaveErrorContext, string> = {
  recording: 'recording',
  release: 'release',
  artists: 'artist changes',
  contributions: 'contributions',
  'release-link': 'release',
};

/** Known raw-error substrings → friendly messages. First match wins. */
const KNOWN_ERRORS: { test: RegExp; message: string }[] = [
  {
    test: /duplicate/i,
    message:
      'This change contains duplicate values (for example a repeated ISRC, label, or catalogue number). Remove the duplicates and try again.',
  },
  {
    test: /accessdenied|not authorized/i,
    message:
      "This change couldn't be saved because of a permissions issue on our side. Please contact support.",
  },
  {
    test: /graph (edges|nodes)|gremlin|expecting value/i,
    message:
      "We couldn't sync this change to the database. Please try again in a moment — if it keeps failing, contact support.",
  },
  {
    test: /opensearch/i,
    message:
      "We couldn't update the search index for this change. Please try again, or contact support if it persists.",
  },
  {
    // Pass the backend's already-friendly message through unchanged.
    test: /duration is already set/i,
    message: 'Duration is already set by the system and cannot be changed.',
  },
];

interface ExtractedError {
  /** Concatenated raw text from the error body (for substring matching). */
  raw: string;
  /** Field names from a DRF validation dict, if any. */
  fields: string[];
  /** True when the body looked like a field-validation dict. */
  isFieldValidation: boolean;
}

function isResponseError(
  err: unknown
): err is { name: string; response: Response } {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: string }).name === 'ResponseError' &&
    'response' in err
  );
}

/** Collect raw text + field names from a parsed error body. */
function extractFromBody(body: unknown): ExtractedError {
  const result: ExtractedError = { raw: '', fields: [], isFieldValidation: false };
  if (typeof body !== 'object' || body === null) {
    if (typeof body === 'string') result.raw = body;
    return result;
  }
  const obj = body as Record<string, unknown>;

  // Service failure shape: { errors: string[] }
  if (Array.isArray(obj.errors)) {
    result.raw = obj.errors.filter((e) => typeof e === 'string').join(' ');
    return result;
  }
  // Misc shape: { error: "…" }
  if (typeof obj.error === 'string') {
    result.raw = obj.error;
    return result;
  }

  // DRF field-error dict, possibly nested under `payload`.
  const dict =
    typeof obj.payload === 'object' && obj.payload !== null
      ? (obj.payload as Record<string, unknown>)
      : obj;
  const parts: string[] = [];
  for (const [field, value] of Object.entries(dict)) {
    if (Array.isArray(value) && value.some((v) => typeof v === 'string')) {
      result.isFieldValidation = true;
      result.fields.push(field);
      parts.push(`${field}: ${value.filter((v) => typeof v === 'string').join(' ')}`);
    }
  }
  result.raw = parts.join(' ');
  return result;
}

export async function friendlySaveError(
  err: unknown,
  context: SaveErrorContext
): Promise<string> {
  let extracted: ExtractedError = { raw: '', fields: [], isFieldValidation: false };

  if (isResponseError(err)) {
    try {
      const body = await err.response.clone().json();
      extracted = extractFromBody(body);
    } catch {
      // Body wasn't JSON / already consumed — fall back to the error message.
      extracted.raw = (err as { message?: string }).message ?? '';
    }
  } else if (err instanceof Error) {
    extracted.raw = err.message;
  }

  const raw = extracted.raw;

  // Field-validation errors (missing required release details).
  if (extracted.isFieldValidation || /may not be null|this field|is required/i.test(raw)) {
    const fieldList = extracted.fields.length
      ? ` Missing or empty: ${extracted.fields.join(', ')}.`
      : '';
    return `Some required details are missing or empty. Please complete every field and try again.${fieldList}`;
  }

  for (const { test, message } of KNOWN_ERRORS) {
    if (test.test(raw)) return message;
  }

  // Generic AWS validation that wasn't a duplicate.
  if (/validationexception/i.test(raw)) {
    return 'The data for this change wasn’t accepted. Please review your edits and try again, or contact support.';
  }

  return `Something went wrong while saving the ${CONTEXT_NOUN[context]}. Your change may not have been saved — please try again, or contact support if the problem continues.`;
}
